import type { AttachmentOut, CorrespondenceOut } from "@/api";

/**
 * Below this a picture is Beiwerk (design 2026-10-24 §6.2, §8.5): signature
 * pictures, logos, the little icons of a mail footer. A photographed sketch
 * is larger — an assumption of the design; a smaller one is not lost, it
 * stays one click away behind the toggle, with „Als Zeichnung übernehmen" in
 * the entry's menu.
 */
export const BEIWERK_MAX_BYTES = 50_000;

/**
 * A picture under 50 kB that is not adopted (§8.5) — whether it *could* be
 * adopted does not matter: signature PNGs can. One that is adopted stays in
 * sight, it is a drawing of the Vorgang now. „Adopted" is what the entry
 * already knows (`TimelineEntry` `inDocuments`), passed in, not a second
 * source.
 */
export function isBeiwerk(attachment: AttachmentOut, adopted: boolean): boolean {
  return attachment.mimeType.startsWith("image/") && attachment.byteSize < BEIWERK_MAX_BYTES && !adopted;
}

/** A mail's attachments in the two halves the entry draws, each in the mail's own order. */
export function splitAttachments(
  list: readonly AttachmentOut[],
  isAdopted: (id: string) => boolean,
): { shown: AttachmentOut[]; beiwerk: AttachmentOut[] } {
  const shown: AttachmentOut[] = [];
  const beiwerk: AttachmentOut[] = [];
  for (const attachment of list) {
    (isBeiwerk(attachment, isAdopted(attachment.id)) ? beiwerk : shown).push(attachment);
  }
  return { shown, beiwerk };
}

/** How Outlook names the pictures of a signature: `image001.png`, `Outlook-k3x2a1.png`. */
const SIGNATURE_NAME = /^(image\d+|outlook-[\w-]+)\.(png|jpe?g|gif)$/i;

/** The noun for the folded pictures — „Signaturbilder" only where every name says so (§8.5). */
function beiwerkNoun(beiwerk: readonly AttachmentOut[]): string {
  const one = beiwerk.length === 1;
  if (beiwerk.every((attachment) => SIGNATURE_NAME.test(attachment.filename))) {
    return one ? "Signaturbild" : "Signaturbilder";
  }
  return one ? "kleines Bild" : "kleine Bilder";
}

/** The toggle while folded: „+ 16 Signaturbilder", „+ 1 kleines Bild". */
export function beiwerkLabel(beiwerk: readonly AttachmentOut[]): string {
  return `+ ${beiwerk.length} ${beiwerkNoun(beiwerk)}`;
}

/** The folded list's own name, „Signaturbilder" or „Kleine Bilder". */
export function beiwerkName(beiwerk: readonly AttachmentOut[]): string {
  const noun = beiwerkNoun(beiwerk);
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
}

/**
 * The same toggle while open, „Signaturbilder ausblenden" — as „Ganzen Text
 * zeigen" turns into „Weniger zeigen" at the text above it.
 */
export function beiwerkHideLabel(beiwerk: readonly AttachmentOut[]): string {
  return `${beiwerkName(beiwerk)} ausblenden`;
}

/**
 * Up to this many files to adopt, each keeps its own „„…“ als Zeichnung
 * übernehmen" in the entry's menu; from one more on, the menu has one item
 * that opens the chooser (UX-Gutachten 2, B-1). A mail with a sketch and
 * sixteen signature pictures listed twenty items — sixteen of them for
 * pictures the record itself had folded away as Beiwerk.
 */
export const ADOPT_ITEMS_MAX = 2;

/** What „als Zeichnung übernehmen" offers: the files the reading takes that are no drawing yet (`adoptable`), in the mail's order. */
export function adoptCandidates(entry: CorrespondenceOut): AttachmentOut[] {
  return entry.attachments.filter((attachment) => attachment.adoptable);
}

/**
 * The chooser's item in the menu: „Bild …" where every file it offers is a
 * picture — the review's words for the case it was found on —, „Anhang …"
 * where a PDF is among them, which no one calls a picture.
 */
export function chooserLabel(candidates: readonly AttachmentOut[]): string {
  const pictures = candidates.every((attachment) => attachment.mimeType.startsWith("image/"));
  return `${pictures ? "Bild" : "Anhang"} als Zeichnung übernehmen …`;
}
